// ============================================================
// RESISTANCES - what an inducer IS, and what a target shrugs off.
//
// Before v8.345.0 every rule carried its own idea of resistance: Toxic
// had an `isMachine` branch and an Astartes re-roll of its own,
// Pinning asked the condition immunity map about 'Pn', and Fearless,
// Resistance (X) and Nerves of Steel were talents whose catalog effects
// nobody read. None of it extended: a hallucinogen gas grenade could
// not ask "does a Machine breathe?" without another branch.
//
// THE MODEL. An inducer names a VECTOR - the channel it works through:
// a toxin in the blood, a gas in the lungs, fear in the mind, a psychic
// touch, a sensory assault, the warp, disease. A target RESISTS vectors,
// not rules: Machine is immune to toxin and gas, an Astartes re-rolls
// against toxin (the Oolitic Kidney), Fearless is immune to fear, and
// Resistance (Psychic Powers) is +10 against psychic. A new inducer picks
// a vector and gets every resistance for free; a new trait names what
// it resists and every inducer honours it.
//
// `resistTest` is the ONE way an inducer asks the question. Immunity
// short-circuits BEFORE the roll - the seeded E2E dice depend on an
// immune subject consuming no die (the pinning contract, kept). A
// re-roll appends one die on failure and reports BOTH rolls, because a
// re-roll with no evidence is invisible (the Oolitic lesson, v8.194.1).
//
// Pure. Zero React dependency.
// ============================================================
import { rollPlainTargetTest, type PlainTargetTestId, type TestResult } from './characteristicTests';
import type { TalentEffect } from './character/talentEffects';

/** The built-in channels. A string outside the list is a custom vector - the Resistance (X) talent's free text. */
export type Vector = 'toxin' | 'gas' | 'fear' | 'psychic' | 'sensory' | 'warp' | 'disease' | (string & {});

export interface VectorDef {
  id: Vector;
  label: string;
  hint: string;
}

export const VECTORS: readonly VectorDef[] = [
  { id: 'toxin', label: 'Toxin', hint: 'in the blood - venoms, poisons, the Toxic quality' },
  { id: 'gas', label: 'Gas', hint: 'in the lungs - gas grenades, spores, a sealed suit keeps it out' },
  { id: 'fear', label: 'Fear', hint: 'in the mind - Fear, Pinning, dread' },
  { id: 'psychic', label: 'Psychic', hint: 'a mind touched by a power' },
  { id: 'sensory', label: 'Sensory', hint: 'the eyes and ears - flash, sonic, phantasms' },
  { id: 'warp', label: 'Warp', hint: 'the immaterium reaching through' },
  { id: 'disease', label: 'Disease', hint: 'infection, rot, plague' },
];

type ResistanceMode = 'immune' | 'reroll' | 'bonus';

export interface Resistance {
  vector: Vector;
  mode: ResistanceMode;
  /** The bonus, for mode 'bonus'. Signed; +10 is the Resistance talent. */
  value?: number;
  /** Where it came from, for the log - 'Machine', 'Fearless', 'Oolitic Kidney'. */
  source: string;
}

/** A target-field-keyed source, the shape `IMMUNITY_MAP` uses. */
export interface ResistanceSource {
  field: string;
  label: string;
  isNumeric: boolean;
  grants: Omit<Resistance, 'source'>[];
}

/**
 * What the built-in traits resist. Fear here is exactly the set the
 * condition immunity map gave Pinning ('Pn'): From Beyond, Machine,
 * Stuff of Nightmares, Frenzied - so the Pinning retrofit changes no
 * answer. Astartes is a RE-ROLL, not immunity: the Oolitic Kidney.
 */
export const RESISTANCE_SOURCES: readonly ResistanceSource[] = [
  {
    field: 'machineAP',
    label: 'Machine',
    isNumeric: true,
    grants: [
      { vector: 'toxin', mode: 'immune' },
      { vector: 'gas', mode: 'immune' },
      { vector: 'fear', mode: 'immune' },
      { vector: 'disease', mode: 'immune' },
    ],
  },
  {
    field: 'fromBeyond',
    label: 'From Beyond',
    isNumeric: false,
    grants: [
      { vector: 'fear', mode: 'immune' },
      { vector: 'psychic', mode: 'immune' },
      { vector: 'sensory', mode: 'immune' },
    ],
  },
  {
    field: 'stuffOfNightmares',
    label: 'Stuff of Nightmares',
    isNumeric: false,
    grants: [
      { vector: 'fear', mode: 'immune' },
      { vector: 'psychic', mode: 'immune' },
      { vector: 'sensory', mode: 'immune' },
    ],
  },
  { field: 'astartes', label: 'Oolitic Kidney', isNumeric: false, grants: [{ vector: 'toxin', mode: 'reroll' }] },
  { field: 'frenzied', label: 'Frenzied', isNumeric: false, grants: [{ vector: 'fear', mode: 'immune' }] },
];

/**
 * The free text a talent was given, as a vector. 'Fear' → fear,
 * 'Psychic Powers' → psychic, 'Poisons' → toxin; anything else is kept
 * as a lower-case custom vector, so 'Heat' resists an inducer that
 * names 'heat' and nothing else.
 */
const VECTOR_WORDS: readonly [RegExp, Vector][] = [
  [/psy/, 'psychic'],
  [/fear|terror|pin/, 'fear'],
  [/tox|poison|venom/, 'toxin'],
  [/gas|spore|inhal/, 'gas'],
  [/disease|plague|rot|infect/, 'disease'],
  [/warp|daemon/, 'warp'],
  [/sens|flash|sonic|blind|deaf/, 'sensory'],
];

export function normaliseVector(text: string): Vector {
  const t = (text ?? '').trim().toLowerCase();
  if (!t) return '';
  const known = VECTOR_WORDS.find(([re]) => re.test(t));
  return known ? known[1] : t.replace(/\s+/g, '-');
}

/**
 * What a sheet's talents and traits resist (v8.346.0) - the resolver
 * hands this to `DefenderCombatStats.resistances`. Three effect kinds
 * arrive here: `vector_resistance` (Fearless), `resistance` (+N against
 * free text, normalised), and `reroll_grant` (its `against` normalised;
 * the Astartes trait's own re-roll is labelled Oolitic Kidney so the
 * flag row below dedupes against it).
 */
export function resistancesFromEffects(effects: readonly TalentEffect[]): Resistance[] {
  return dedupe(effects.map(resistanceOfEffect).filter((r): r is Resistance => r !== null));
}

/** One talent effect's resistance, or null when it grants none. */
function resistanceOfEffect(e: TalentEffect): Resistance | null {
  if (e.type === 'vector_resistance') {
    return { vector: e.vector, mode: e.mode, ...(e.value !== undefined ? { value: e.value } : {}), source: e.label };
  }
  if (e.type !== 'resistance' && e.type !== 'reroll_grant') return null;
  const vector = normaliseVector(e.against);
  if (!vector) return null;
  return e.type === 'resistance'
    ? { vector, mode: 'bonus', value: e.value, source: `Resistance (${e.against})` }
    : { vector, mode: 'reroll', source: e.label ?? `Re-roll (${e.against})` };
}

/** One row per (vector, mode, source) - a trait flag and its own talent effect say the same thing once. */
function dedupe(rows: Resistance[]): Resistance[] {
  const seen = new Set<string>();
  return rows.filter((r) => {
    const key = `${r.vector}|${r.mode}|${r.source}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Whether a target-field-keyed source is on: a numeric field above 0, any
 * other field truthy. The shape `IMMUNITY_MAP` and `RESISTANCE_SOURCES` share.
 */
export function sourceActive(target: Record<string, unknown>, src: { field: string; isNumeric: boolean }): boolean {
  const val = target[src.field];
  return src.isNumeric ? typeof val === 'number' && val > 0 : !!val;
}

/** The trait flags plus whatever the sheet resolved from talents (`target.resistances`). */
export function resistancesOf(target: Record<string, unknown>): Resistance[] {
  const fromFlags = RESISTANCE_SOURCES.filter((src) => sourceActive(target, src)).flatMap((src) =>
    src.grants.map((g) => ({ ...g, source: src.label })),
  );
  const own = Array.isArray(target.resistances)
    ? (target.resistances as Resistance[]).filter((r) => r && r.vector)
    : [];
  return dedupe([...fromFlags, ...own]);
}

export interface ResistanceSummary {
  immune: boolean;
  reroll: boolean;
  /** Every bonus summed - two +10 talents are +20. */
  bonus: number;
  immuneSources: string[];
  rerollSources: string[];
  bonusSources: string[];
}

/** Everything the target has against one vector, folded into one answer. */
export function resistanceTo(target: Record<string, unknown>, vector: Vector): ResistanceSummary {
  const all = resistancesOf(target).filter((r) => r.vector === vector);
  const by = (mode: ResistanceMode) => all.filter((r) => r.mode === mode);
  return {
    immune: by('immune').length > 0,
    reroll: by('reroll').length > 0,
    bonus: by('bonus').reduce((n, r) => n + (r.value ?? 0), 0),
    immuneSources: by('immune').map((r) => r.source),
    rerollSources: by('reroll').map((r) => r.source),
    bonusSources: by('bonus').map((r) => r.source),
  };
}

export interface ResistOutcome {
  /** Nothing was rolled: the target cannot be touched through this vector. */
  immune: boolean;
  /** What the target had against the vector, for the log. */
  resistance: ResistanceSummary;
  /** The test that decided it - the re-roll when there was one. Null when immune. */
  test: TestResult | null;
  /** The failed first roll a re-roll replaced, so the log can show both. */
  firstTest: TestResult | null;
  rerolled: boolean;
  /** Shrugged off - immune, or the deciding test passed. */
  passed: boolean;
}

/**
 * Roll one subject's test against an inducer of `vector`.
 *
 * `ignoreImmunity` is Purgatus and its kin: the inducer reaches even
 * what cannot normally be reached. A re-roll and a bonus still apply -
 * ignoring immunity is not ignoring toughness.
 */
export function resistTest(
  target: Record<string, unknown>,
  vector: Vector,
  testId: PlainTargetTestId,
  difficultyMod: number,
  customMod: number,
  opts: { ignoreImmunity?: boolean } = {},
): ResistOutcome {
  const resistance = resistanceTo(target, vector);
  if (resistance.immune && !opts.ignoreImmunity) {
    return { immune: true, resistance, test: null, firstTest: null, rerolled: false, passed: true };
  }
  const t = target as unknown as Parameters<typeof rollPlainTargetTest>[0];
  const first = rollPlainTargetTest(t, testId, difficultyMod, customMod + resistance.bonus);
  if (first.success || !resistance.reroll) {
    return { immune: false, resistance, test: first, firstTest: null, rerolled: false, passed: first.success };
  }
  const second = rollPlainTargetTest(t, testId, difficultyMod, customMod + resistance.bonus);
  return { immune: false, resistance, test: second, firstTest: first, rerolled: true, passed: second.success };
}

/**
 * One line for a log: "immune (Machine)", "Pass T-test (34 vs 60)",
 * "FAIL T-test (71 vs 60) - Oolitic Kidney re-roll: 22 - Pass",
 * "Pass WP test (34 vs 50, +10 Resistance)".
 */
export function describeResist(o: ResistOutcome, testLabel: string): string {
  const r = o.resistance;
  if (o.immune) return `immune (${r.immuneSources.join(', ')})`;
  const t = o.test!;
  const bonus = r.bonus ? `, ${r.bonus > 0 ? '+' : ''}${r.bonus} ${r.bonusSources.join('/')}` : '';
  if (!o.rerolled) return `${t.success ? 'Pass' : 'FAIL'} ${testLabel} (${t.roll} vs ${t.target}${bonus})`;
  const f = o.firstTest!;
  const who = r.rerollSources.join('/');
  return `FAIL ${testLabel} (${f.roll} vs ${f.target}${bonus}) - ${who} re-roll: ${t.roll} - ${t.success ? 'Pass' : 'FAIL'}`;
}
